'use client';

import { useRef, useEffect, useState } from 'react';
import Image from 'next/image';

import { CountDownTimer, PlayerBall } from '../../components';

/**
 * Represents the props for the MotionSensorTest component.
 */
export interface IMotionSensorsProps {
  duration: number;
  onContinue: () => void;
  onTimeout: () => void;
}

/**
 * -----------------------------------------------------------------------------
 * Setup the functionalities to test the sensor.
 */
export function MotionSensorTest({
  duration,
  onTimeout,
  onContinue,
}: IMotionSensorsProps) {
  const ballSize = 64;
  const [beta, setBeta] = useState<number | null>(null);
  const [gamma, setGamma] = useState<number | null>(null);
  const [ballPosition, setBallPosition] = useState({ x: 0, y: 0 });
  const [score, setScore] = useState(0);
  const [winDisplayed, setWinDisplayed] = useState(false);

  const maxXRef = useRef<number>(0);
  const maxYRef = useRef<number>(0);

  // Refs for the goals
  const goalRefs = useRef<Array<HTMLImageElement | null>>([null, null, null]);

  /**
   * Function to handle assessment done
   */
  const handleOnAssessmentDone = () => {
    onContinue();
  };

  /**
   * Function to handle assessment timeout
   */
  const handleOnAssessmentTimeout = () => {
    onTimeout();
  };

  /**
   * Effect to handle device orientation event
   */
  useEffect(() => {
    function handleOrientation(event: DeviceOrientationEvent) {
      const newBeta = event.beta;
      const newGamma = event.gamma;

      if (newBeta !== null && newGamma !== null) {
        setBeta(newBeta);

        setGamma(newGamma);
      }
    }

    if ('DeviceOrientationEvent' in window) {
      window.addEventListener('deviceorientation', handleOrientation);
    } else {
      // eslint-disable-next-line no-alert
      alert('Motion sensor not supported');
    }

    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, []);

  /**
   * Effect to set max X and Y references
   */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      maxXRef.current = window.innerWidth - ballSize;
      maxYRef.current = window.innerHeight - ballSize;
    }
  }, []);

  /**
   * Effect to handle ball movement and collision detection
   */
  useEffect(() => {
    if (beta !== null && gamma !== null) {
      const boundedX = Math.min(
        Math.max(ballPosition.x + gamma * 1.5, 0),
        maxXRef.current
      );
      const boundedY = Math.min(
        Math.max(ballPosition.y + beta * 1.5, 0),
        maxYRef.current
      );

      setBallPosition({ x: boundedX, y: boundedY });

      goalRefs.current.forEach((goalRef, index) => {
        if (goalRef) {
          const circleBounds = goalRef.getBoundingClientRect();
          const ballBounds = {
            left: boundedX + 30,
            top: boundedY + 30,
          };

          const circleCenterX = circleBounds.left + circleBounds.width / 2;
          const circleCenterY = circleBounds.top + circleBounds.height / 2;
          const distance = Math.sqrt(
            (ballBounds.left - circleCenterX) ** 2 +
              (ballBounds.top - circleCenterY) ** 2
          );

          if (distance < 70) {
            setScore((prevScore) => prevScore + 1);
            // eslint-disable-next-line no-param-reassign
            goalRef.style.display = 'none';
            goalRefs.current[index] = null;
          }
        }
      });
    }

    if (score === 3 && !winDisplayed) {
      handleOnAssessmentDone();

      setWinDisplayed(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beta, gamma, score, winDisplayed]);

  /**
   * Function to handle goal reference
   */
  const handleOnGoalRef = (index: number) => (ref: HTMLImageElement | null) => {
    goalRefs.current[index] = ref;
  };

  return (
    <div className="relative h-full w-full">
      <Image
        alt="Grass"
        className="absolute -z-10 h-full w-full object-cover"
        fill
        priority
        src="/images/assessment-images/grass.png"
      />
      <PlayerBall
        className="absolute h-16 w-16 transition-all duration-300 ease-in-out"
        style={{
          left: `${ballPosition.x}px`,
          top: `${ballPosition.y}px`,
        }}
      />
      <Image
        ref={handleOnGoalRef(0)}
        alt="Circle"
        className="absolute right-6 top-8"
        height={98}
        src="/images/svg/circle1.svg"
        width={93}
      />
      <Image
        ref={handleOnGoalRef(1)}
        alt="Circle"
        className="absolute"
        height={168}
        src="/images/svg/circle2.svg"
        style={{ top: '50%', left: 0, transform: 'translateY(-50%)' }}
        width={169}
      />
      <Image
        ref={handleOnGoalRef(2)}
        alt="Circle"
        className="absolute bottom-1 right-0"
        height={168}
        src="/images/svg/circle3.svg"
        width={177}
      />
      <CountDownTimer
        duration={duration}
        onTimeout={handleOnAssessmentTimeout}
      />
    </div>
  );
}
